import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import useKothar from "context/useKothar";
import { differenceInDays } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

import EmailTemplatesModal from "./EmailTemplatesModal";
import { hasPermission } from "utils/permissionUtils";
import { P } from "const/permissions";

const FROM_EMAILS = [
  "admissions@kotharedu.com",
  "info@kotharedu.com",
  "visa@kotharedu.com",
  "accounts@kotharedu.com",
];

const ComposeEmailDrawer = ({ open, onClose, recipient, message }) => {
  const [from, setFrom] = useState(FROM_EMAILS[1]);
  const [to, setTo] = useState([]);
  const [toCategory, setToCategory] = useState(null);
  const [cc, setCc] = useState([]);
  const [tags, setTags] = useState([]);
  const [template, setTemplate] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState(message);
  const [attachments, setAttachments] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElBody, setAnchorElBody] = useState(null);
  const subjectRef = useRef();
  const quillRef = useRef();
  const [custom, setCustom] = useState(false);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [externalEmailsPaste, setExternalEmailsPaste] = useState(recipient ?? "");
  const [festivalName, setFestivalName] = useState("");
  const rawTemplateRef = useRef({ subject: "", body: "" });
  const [{ clientList = [] }, { refetchClient }] = useKothar();

  const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (open) {
      if (recipient) setExternalEmailsPaste(recipient);
      if (message) setBody(message);
      if (!clientList?.length) refetchClient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Parse pasted text into unique valid emails (Excel: newlines, tabs, commas)
  const parseExternalEmails = (text) => {
    const { valid } = parseExternalEmailsWithInvalid(text);
    return valid;
  };

  // Returns { valid: string[], invalid: string[] } for UI feedback
  const parseExternalEmailsWithInvalid = (text) => {
    if (!text?.trim()) return { valid: [], invalid: [] };
    const raw = text
      .split(/[\r\n\t,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const valid = [];
    const invalid = [];
    const seenValid = new Set();
    const seenInvalid = new Set();
    raw.forEach((e) => {
      if (emailLike.test(e)) {
        if (!seenValid.has(e)) {
          seenValid.add(e);
          valid.push(e);
        }
      } else {
        if (!seenInvalid.has(e)) {
          seenInvalid.add(e);
          invalid.push(e);
        }
      }
    });
    return { valid, invalid };
  };

  const getEmailTemplates = async () => {
    const response = await axios.get("/email-templates");
    return response?.data;
  };

  const { data: emailTemplates = [], refetch: refetchEmailTemplates } = useQuery(
    ["emailTemplates"],
    getEmailTemplates,
    {
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  const { mutate: sendEmail, isLoading } = useMutation({
    mutationFn: (data) => axios.post("/send-email/bulk", data),
    onSuccess: () => {
      toast.success("Email sent to the clients!");
      setCustom(false);
    },
    onError: () => {
      toast.error("Failed to send email");
      setCustom(false);
    },
  });

  const clientWithValidEmail = clientList?.filter((client) => {
    return client.email?.length > 0;
  });

  const clientWithVisaExpiry = clientList?.filter((client) => {
    const daysUntilExpiry = differenceInDays(new Date(client.visaExpiry), new Date());
    return client.visaExpiry && daysUntilExpiry < 90 && daysUntilExpiry > 0;
  });
  const clientWithBirthdayToday = clientList?.filter((client) => {
    const today = new Date();
    const birthday = new Date(client.dob);
    return birthday.getMonth() === today.getMonth() && birthday.getDate() === today.getDate();
  });

  const clientWithPassportExpiry = clientList?.filter((client) => {
    const daysUntilExpiry = differenceInDays(new Date(client.passportExpiry), new Date());
    return client.passportExpiry && daysUntilExpiry < 90 && daysUntilExpiry > 0;
  });

  const isFestivalTemplate = useMemo(
    () => emailTemplates?.find((t) => t.id === template)?.templateName === "Festival Greetings",
    [template, emailTemplates]
  );

  const applyTemplate = (tpl) => {
    rawTemplateRef.current = { subject: tpl?.subject || "", body: tpl?.body || "" };
    setFestivalName("");
    setSubject(tpl?.subject || "");
    setBody(tpl?.body || "");
  };

  // Handle template selection
  const handleTemplateChange = (val) => {
    const tpl = emailTemplates?.find((t) => t?.id === val?.value);
    setTemplate(tpl?.id || null);
    applyTemplate(tpl);
  };

  // Handle template selection from modal
  const handleTemplateSelectFromModal = (selectedTemplate) => {
    setTemplate(selectedTemplate.id);
    applyTemplate(selectedTemplate);
    setCustom(false);
  };

  const handleFestivalNameChange = (e) => {
    const name = e.target.value;
    setFestivalName(name);
    setSubject(rawTemplateRef.current.subject.replace(/\{festivalName\}/g, name));
    setBody(rawTemplateRef.current.body.replace(/\{festivalName\}/g, name));
  };

  // Send handler (stub)
  const handleSend = (e) => {
    e.preventDefault();
    if (!from || !subject || !body) {
      toast.error("Please fill all required fields");
      return;
    }
    if (isFestivalTemplate && !festivalName.trim()) {
      toast.error("Please enter the festival name before sending");
      return;
    }
    if (body.includes("{offerDetails}")) {
      toast.error("Replace {offerDetails} with actual offer details before sending");
      return;
    }
    if (to.length === 0 && externalEmailsPaste.trim() === "") {
      toast.error("Please select at least one recipient or paste external emails");
      return;
    }
    // The To box is free-typed: a hand-typed name has no client id and would
    // otherwise fail per-recipient as "client-id:null". Skip those entries.
    const clientIds = to
      .map((t) => (t && typeof t === "object" ? t.id : null))
      .filter((id) => id !== null && id !== undefined);
    const ccRecipients = Array.isArray(cc)
      ? cc.map((c) => (typeof c === "string" ? c : c?.email ?? c)).filter(Boolean)
      : [];
    const bccRecipients = []; // add bcc state if needed

    const externalEmails = parseExternalEmails(externalEmailsPaste);

    if (clientIds.length === 0 && externalEmails.length === 0) {
      toast.error("No valid client selected — pick clients from the list or paste external emails");
      return;
    }
    if (clientIds.length < to.length) {
      toast.warn(
        `${to.length - clientIds.length} selected ${
          to.length - clientIds.length === 1 ? "entry was" : "entries were"
        } not linked to a client record and ${
          to.length - clientIds.length === 1 ? "was" : "were"
        } skipped`
      );
    }

    const request = {
      clientIds,
      externalEmails,
      templateId: custom ? 0 : template ?? 0,
      ccRecipients,
      bccRecipients,
      customSubject: custom ? subject : "",
      customBody: custom ? body : "",
      fromEmail: from ?? "",
      tags,
      external: externalEmails.length > 0,
      attachmentKeys: attachments?.attachments?.map((attachment) => attachment),
    };

    sendEmail(request);
  };

  const filteredList = useMemo(() => {
    if (toCategory?.value === "all") {
      return clientWithValidEmail;
    }

    if (toCategory?.value === "visaExpiry") {
      return clientWithVisaExpiry;
    }
    if (toCategory?.value === "birthday") {
      return clientWithBirthdayToday;
    }
    if (toCategory?.value === "passportExpiry") {
      return clientWithPassportExpiry;
    }

    return clientList;
  }, [toCategory]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 750 } } }}
    >
      <div className="flex items-center justify-between border-b border-primary px-4 py-4">
        <Typography variant="h6">Compose Email</Typography>
        <IconButton onClick={onClose}>
          <IoCloseCircleOutline className="text-4xl" />
        </IconButton>
      </div>
      <Box
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minWidth: 350,
        }}
      >
        <Typography variant="caption" color="text.secondary" mb={2}>
          * fields are mandatory.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <form className="flex flex-col gap-4">
          <Autocomplete
            options={FROM_EMAILS}
            value={from}
            onChange={(e, val) => setFrom(val)}
            renderInput={(params) => <TextField {...params} label="From" />}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => {
              return option === value;
            }}
            size="small"
          />

          <Autocomplete
            options={[
              {
                label: "All Clients with valid email",
                value: "all",
              },
              {
                label: "Clients with Visa Expiry",
                value: "visaExpiry",
              },
              {
                label: "Clients with Birthday Today",
                value: "birthday",
              },
              {
                label: "Clients with Passport Expiry",
                value: "passportExpiry",
              },
            ]}
            size="small"
            value={toCategory ?? null}
            onChange={(_, val) => {
              setToCategory(val);
              if (val?.value === "all") {
                setTo(clientWithValidEmail);
              } else if (val?.value === "visaExpiry") {
                setTo(clientWithVisaExpiry);
              } else if (val?.value === "birthday") {
                setTo(clientWithBirthdayToday);
              } else if (val?.value === "passportExpiry") {
                setTo(clientWithPassportExpiry);
              } else {
                setTo([]);
              }
            }}
            renderInput={(params) => <TextField {...params} label="Select the Category" />}
            isOptionEqualToValue={(option, value) => {
              return option.value === value.value;
            }}
          />

          <Typography variant="caption" color="text.secondary">
            Selected {toCategory?.label}
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={[...filteredList]}
            value={to}
            size="small"
            onChange={(_, val) => setTo(val)}
            renderInput={(params) => <TextField {...params} label="To *" />}
            getOptionLabel={(option) => option?.name}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option?.id}>
                <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex items-center gap-2">
                    {/* <CheckBox checked={selected} style={{ marginRight: 8 }} /> */}
                    {/* <Avatar src={option?.avatar} /> */}
                    {option?.email ? `${option?.name} (${option?.email})` : option?.name || ""}
                  </div>
                </div>
              </li>
            )}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            limitTags={3}
            disableCloseOnSelect={true}
          />

          <div>
            <label className="block text-sm font-medium text-app-text mb-1">
              External emails (optional)
            </label>
            <textarea
              value={externalEmailsPaste}
              onChange={(e) => setExternalEmailsPaste(e.target.value)}
              placeholder="Paste from Excel — one per line, or comma / tab separated. Only valid emails are used."
              rows={4}
              className="w-full px-3 py-2 border border-app-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm resize-y min-h-[80px]"
            />
            {externalEmailsPaste.trim() &&
              (() => {
                const { valid, invalid } = parseExternalEmailsWithInvalid(externalEmailsPaste);
                return (
                  <div className="mt-1.5 space-y-1">
                    {valid.length > 0 && (
                      <p className="text-xs text-app-text-secondary">
                        <span className="font-medium text-green-600">{valid.length} valid</span>
                        {valid.length !== 1 ? " emails" : " email"} will be added as recipients
                      </p>
                    )}
                    {invalid.length > 0 && (
                      <p className="text-xs text-red-600 flex items-start gap-1">
                        <span className="font-medium shrink-0">⚠</span>
                        <span>
                          <span className="font-medium">{invalid.length} invalid</span> (ignored):
                          {invalid.slice(0, 5).join(", ")}
                          {invalid.length > 5 && ` +${invalid.length - 5} more`}
                        </span>
                      </p>
                    )}
                  </div>
                );
              })()}
          </div>

          <div className="flex items-center gap-2">
            <Autocomplete
              options={emailTemplates.map((tpl) => ({
                templateName: tpl.templateName,
                value: tpl.id,
              }))}
              value={emailTemplates.find((tpl) => tpl.id === template) || null}
              size="small"
              onChange={(_, val) => {
                handleTemplateChange(val);
              }}
              renderInput={(params) => <TextField {...params} label="Template" />}
              getOptionLabel={(option) => option.templateName}
              isOptionEqualToValue={(option, value) => {
                return option.value === value.id;
              }}
              // sx={{ width: "300px" }}
              className="flex-1"
            />
            {hasPermission(P.EMAIL.TEMPLATE_CREATE) && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setTemplatesModalOpen(true)}
              >
                Manage Templates
              </Button>
            )}
          </div>

          {isFestivalTemplate && (
            <TextField
              label="Festival Name *"
              value={festivalName}
              onChange={handleFestivalNameChange}
              size="small"
              fullWidth
              required
              placeholder="e.g. Dashain, Christmas, Eid"
              helperText="Required — replaces {festivalName} in subject and body"
              error={!festivalName.trim()}
            />
          )}

          <div className="relative">
            <InputField
              label="Subject"
              value={subject}
              onChange={(e) => {
                setCustom(true);
                setSubject(e.target.value);
              }}
              inputRef={subjectRef}
              required
            />
          </div>

          <Autocomplete
            multiple
            freeSolo
            options={["Admission", "Visa", "Accounts", "Follow Up", "Urgent"]}
            value={tags}
            size="small"
            onChange={(_, val) => setTags(val)}
            renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tag" />}
            limitTags={4}
          />

          <div>
            <label className="block mb-1 font-medium">Message</label>
            <ReactQuill
              ref={quillRef}
              value={body}
              onChange={(val) => {
                setCustom(true);
                setBody(val);
              }}
              // modules={quillModules}
              placeholder="Compose your message. Use the toolbar to add links or image URLs."
            />
            {body.includes("{offerDetails}") && (
              <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                ⚠ Replace <strong>{"{offerDetails}"}</strong> with actual offer details before
                sending.
              </p>
            )}
          </div>
          <UploadFile
            label="Attachment"
            data={attachments}
            setData={setAttachments}
            imageKey="attachments"
            onChange={(e) => setAttachments(Array.from(e.target.files))}
            type="multiple"
          />
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} /> : null}
              onClick={handleSend}
            >
              {isLoading ? "Sending..." : "Send"}
            </Button>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
          </Box>
        </form>
      </Box>
      <EmailTemplatesModal
        open={templatesModalOpen}
        onClose={() => setTemplatesModalOpen(false)}
        onTemplateSelect={handleTemplateSelectFromModal}
        refetchTemplates={refetchEmailTemplates}
      />
    </Drawer>
  );
};

export default ComposeEmailDrawer;
